import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
// import 'tabulator-tables/dist/css/tabulator.min.css';
import 'react-tabulator/lib/styles.css'; // default theme
import 'react-tabulator/css/tabulator_bootstrap5.min.css';
import { ReactTabulator } from 'react-tabulator';
import { CSVLink } from 'react-csv';
// Asset fetching handled via custom hook
import { PatchAssets } from "../api/assetUpdateService";
import { useGlobalProvider } from "../contexts/GlobalProvider";
import { useAuth } from '../contexts/AuthContext';
import { useAssets } from "../hooks/useAssets";
import { useLinkedData } from '../hooks/useLinkedData';
import { assetModes } from '../utilities/assetModes';
import { createRowMenu } from '../utilities/rowMenuDefinitions';
import useExcelIntegration from '../hooks/useExcelIntegration';
import { createTableColumns } from '../utilities/createTableColumns';
import createExpandRowFormatter from '../utilities/createExpandRowFormatter';
import { FetchAssets } from '../api/assetService';




/**
 * Complex asset table built using React Tabulator. It supports
 * inline editing, pagination, linking with the viewer and exporting
 * data as CSV. The table is primarily controlled through the
 * `useAssets` hook for fetching data and exposes various callbacks
 * via props.
 * 
 * TOGGLE ROW FUNCTIONALITY:
 * - Supports expandable rows that show additional data from external sources
 * - Integrates with Excel files and Airtable through the useLinkedData hook
 * - Toggle functionality is controlled by asset modes (link2Excel, link2AirTable)
 * - Exposes toggle functions globally via window.assetDataProps for viewer extensions
 * - Creates expand columns when external data sources are linked
 * 
 * GLOBAL DATA EXPOSURE:
 * - Exposes table data, options, and toggle functions to window.assetDataProps
 * - This allows viewer extensions like ViewerTable to access the same data and functionality
 * - Updates global data whenever table state changes
 *
 * @param {Object} props Component props
 * @param {boolean} [props.linkViewersEnabled=false] Enable viewer linking
 * @param {React.Ref} ref Ref exposing Tabulator API
 */

/**
 * Main asset table component rendered using Tabulator.
 */
const TabulatorTable = forwardRef(({ linkViewersEnabled = false }, ref) => {
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [assetUpdated, setAssetUpdated] = useState(false);
    const [changes, setChanges] = useState({});
    const [editableRowIndex, setEditableRowIndex] = useState(null);
    const tableRef = useRef(null);
    // Grouping state
    const [groupField, setGroupField] = useState("");
    const [secondaryGroupField, setSecondaryGroupField] = useState("");
    const { selectedProjectId, selectedCategoryId, selectedTagId, setSelectedTagId, setSelectedAssetId, setSelectedTagIdViewerLeft, setSelectedTagIdViewerRight, selectedGroupData, setSelectedGroupData, accCurrentNode } = useGlobalProvider();
    const [selectedRowId, setSelectedRowId] = useState(null); // Track selected row ID
    const [assetFetchOptions, setAssetFetchOptions] = useState('?includeCustomAttributes=true&limit=200');
    const [reloadFlag, setReloadFlag] = useState(false);
    const [selectedAssetMode, setSelectedAssetMode] = useState('');

    const {
        excelHeaders,
        excelRows,
        selectedExcelColumn,
        isExcelLinked,
        linkedExcelFileName,
        handleLinkExcelClick,
        handleExcelColumnSelect,
        handleClearExcelLink,
        excelOptions,
        loadExcelSettings,
    } = useExcelIntegration();

    const [actions, setActions] = useState({
        link2AirTable: false,
        link2Excel: false
    });

    useEffect(() => {
        console.log('Actions state updated asset table:', actions);
        // setReloadFlag(prevFlag => !prevFlag);
    }, [actions]);

    // Load asset mode from localStorage on mount
    useEffect(() => {
        const savedMode = loadAssetMode();
        if (savedMode) {
            setSelectedAssetMode(savedMode);
            // Set actions based on saved mode
            const newActions = {
                link2AirTable: false,
                link2Excel: false
            };
            if (savedMode) {
                newActions[savedMode] = true;
            }
            setActions(newActions);
        }
    }, []);

    // Save asset mode when it changes
    useEffect(() => {
        if (selectedAssetMode) {
            saveAssetMode(selectedAssetMode);
        }
    }, [selectedAssetMode]);


    // Track when selectedTagId changes to potentially trigger search
    const [pendingTagSearch, setPendingTagSearch] = useState(null);
    // Track if a reload was triggered due to viewer linking to avoid
    // fetching the same asset twice
    const linkTriggeredReload = useRef(false);

    // Clear group context when grouping fields change
    useEffect(() => {
        console.log("Grouping fields changed, clearing group context");
        setSelectedGroupData(null);
    }, [groupField, secondaryGroupField, setSelectedGroupData]);

    // Log when group context changes
    useEffect(() => {
        console.log("Selected group data changed:", selectedGroupData);
    }, [selectedGroupData]);

    // set clicked viewer objects id to tagid to search for - only when Link Viewers is enabled
    useEffect(() => {
        console.log("selectedTagId changed in AssetTable:", selectedTagId, "linkViewersEnabled:", linkViewersEnabled);
        if (linkViewersEnabled && selectedTagId && selectedTagId.trim() !== '') {
            setPendingTagSearch(selectedTagId);
        } else {
            setPendingTagSearch(null);
        }
    }, [selectedTagId, linkViewersEnabled]);
    // Clear group context when category changes since data will be different
    useEffect(() => {
        console.log("Category changed in AssetTable - new selectedCategoryId:", selectedCategoryId);
        console.log("Category changed, clearing group context");
        setSelectedGroupData(null);
    }, [selectedCategoryId, setSelectedGroupData]);

    const { getAccessToken } = useAuth();

    // Fetch assets and related metadata via custom hook
    const { data, statusOptions, categoryOptions, customPropsMap, locationsMap, categoryCustomAttributes, isLoading, error, progress, cancelFetch, canCancel } = useAssets(selectedProjectId, selectedCategoryId, assetFetchOptions, reloadFlag, null, getAccessToken);

    // State to manage additional assets fetched individually
    const [additionalAssets, setAdditionalAssets] = useState([]);

    // Combine main data with additional assets
    const combinedData = [...data, ...additionalAssets];

    // Data used for rendering the table - allows clearing the view
    const [tableData, setTableData] = useState(combinedData);

    // Keep tableData in sync with fetched data unless the view was cleared
    useEffect(() => {
        setTableData([...data, ...additionalAssets]);
    }, [data, additionalAssets]);

    // Define hidden keys that should never be shown, even with "Show All Columns"
    const hiddenKeys = ['id', 'isActive', 'updatedAt', 'createdAt', 'createdBy', 'updatedBy', 'companyId', 'customAttributes', 'version'];

    const headerContextMenu = [
        {
            label: "Hide Column",
            action: function (e, column) {
                console.log("Hiding column");
                column.hide();
            }
        },
        {
            label: "Show All Columns",
            action: function (e, column) {
                console.log("Showing all visible columns (excluding system columns)");
                column.getTable().getColumns().forEach(function (col) {
                    const fieldName = col.getField();
                    // Only show columns that are not in the hiddenKeys array
                    if (!hiddenKeys.includes(fieldName)) {
                        col.show();
                    }
                });
            }
        },
    ];



    // Hooks for different linked data sources
    const { toggleRow: toggleRowAirtable } = useLinkedData('airtable');
    const { toggleRow: toggleRowExcel } = useLinkedData('excel', excelOptions);

    const expandRow = createExpandRowFormatter();

    /**
     * COLUMN CREATION WITH TOGGLE ROW SUPPORT
     * 
     * Creates table columns with conditional expand column based on active asset modes.
     * The expand column is only included when external data sources are linked
     * (Excel or Airtable integration is active).
     * 
     * The expand column uses the expandRow formatter which creates clickable
     * buttons that trigger the toggle row functionality to show/hide additional
     * data from external sources.
     */
    const createColumns = () => {
        const includeExpandColumn = actions['link2AirTable'] || actions['link2Excel'];

        if (includeExpandColumn) {
            console.log("Expand column added - actions:", actions);
        } else {
            console.log("No expand column - actions:", actions);
        }

        return createTableColumns(combinedData, customPropsMap, statusOptions, locationsMap, categoryOptions, {
            includeExpandColumn: includeExpandColumn,
            expandFormatter: expandRow,
            headerContextMenu: headerContextMenu,
            editableRowIndex: editableRowIndex,
            categoryCustomAttributes: categoryCustomAttributes
        });
    };

    const columns = createColumns();

    /**
     * Get available columns for grouping with their display names
     * Filters out system columns and the expand column
     */
    const getGroupingColumns = useMemo(() => {
        if (!columns || columns.length === 0) return [];

        return columns
            .filter(col => {
                // Filter out system columns, expand column, and columns without proper field names
                return col.field &&
                    col.field !== 'expand' &&
                    col.visible !== false &&
                    !hiddenKeys.includes(col.field);
            })
            .map(col => ({
                field: col.field,
                title: col.title || col.field
            }));
    }, [columns]);

    /**
     * GLOBAL DATA EXPOSURE FOR VIEWER EXTENSIONS
     * 
     * Exposes current table data and functionality to window.assetDataProps
     * so that viewer extensions (like ViewerTable) can access the same data
     * and toggle functionality without prop drilling.
     * 
     * Exposed data includes:
     * - Table data (filtered and unfiltered)
     * - Options for dropdowns (status, category, location)
     * - Custom properties mapping
     * - Column definitions and context menus
     * - Asset mode and Excel integration data
     * - Toggle functions for row expansion (Excel and Airtable)
     * - Actions state for UI integration
     * 
     * This global exposure enables seamless integration between the main
     * AssetTable and viewer extensions without complex prop passing.
     */
    useEffect(() => {
        if (!window.assetDataProps) {
            window.assetDataProps = {};
        }
        if (window.assetDataProps) {
            window.assetDataProps.currentTableData = tableData;
            window.assetDataProps.unfilteredTableData = combinedData;
            window.assetDataProps.statusOptions = statusOptions;
            window.assetDataProps.categoryOptions = categoryOptions;
            window.assetDataProps.customPropsMap = customPropsMap;
            window.assetDataProps.locationsMap = locationsMap;
            window.assetDataProps.categoryCustomAttributes = categoryCustomAttributes;
            window.assetDataProps.columns = columns;
            window.assetDataProps.headerContextMenu = headerContextMenu;
            window.assetDataProps.selectedAssetMode = selectedAssetMode;
            window.assetDataProps.excelRows = excelRows;
            window.assetDataProps.selectedExcelColumn = selectedExcelColumn;
            window.assetDataProps.toggleRowExcel = toggleRowExcel;
            window.assetDataProps.toggleRowAirtable = toggleRowAirtable;
            // Also expose the actions state for better integration
            window.assetDataProps.actions = actions;
            console.log('AssetTable: window.assetDataProps updated', window.assetDataProps);
        }
    }, [
        tableData,
        combinedData,
        statusOptions,
        categoryOptions,
        customPropsMap,
        locationsMap,
        categoryCustomAttributes,
        columns,
        headerContextMenu,
        selectedAssetMode,
        excelRows,
        selectedExcelColumn,
        toggleRowExcel,
        toggleRowAirtable,
        actions,
    ]);


    /**
     * Fetch a single asset by search term and append it to the table if it
     * does not already exist. This is used when a tag is entered that was not
     * part of the initial fetch results.
     *
     * @param {string} searchTerm Text to search for
     */
    const fetchAndAddAsset = async (searchTerm) => {
        try {
            const accessToken = await getAccessToken();
            const searchOptions = `?filter[searchText]=${searchTerm}&includeCustomAttributes=true&includeDeleted=false&limit=10`;

            console.log('Fetching specific asset with options:', searchOptions);



            const params = {
                selectedProjectId,
                selectedCategoryId,
                accessToken
            };

            const response = await FetchAssets(params, searchOptions);



            if (response && response.results && response.results.length > 0) {
                // Process the fetched assets similar to how useAssets does it
                const processedAssets = response.results.map(item => ({
                    ...item,
                    ...item.customAttributes,
                    categoryId: categoryOptions[item.categoryId] || item.categoryId,
                    statusId: statusOptions[item.statusId] || item.statusId,
                    locationId: locationsMap[item.locationId] || item.locationId
                }));

                // Filter out assets that already exist in current data
                const newAssets = processedAssets.filter(newAsset =>
                    !data.some(existingAsset => existingAsset.id === newAsset.id) &&
                    !additionalAssets.some(existingAsset => existingAsset.id === newAsset.id)
                );

                if (newAssets.length > 0) {
                    console.log('Adding new assets to table:', newAssets);
                    setAdditionalAssets(prev => [...prev, ...newAssets]);

                    // Highlight the first matching asset
                    const matchingAsset = newAssets.find(asset =>
                        asset.clientAssetId && asset.clientAssetId.toLowerCase().includes(searchTerm.toLowerCase())
                    );

                    if (matchingAsset && tableRef.current) {
                        setTimeout(() => {
                            try {
                                const table = tableRef.current.table;
                                const row = table.getRow(matchingAsset.id);
                                if (row) {
                                    row.scrollTo();
                                    handleRowClick(null, row);
                                }
                            } catch (error) {
                                console.log("Could not highlight newly added row:", error);
                            }
                        }, 200);
                    }
                } else {
                    console.log('No new assets found or all assets already exist in table');
                }
            } else {
                console.log('No assets found for search term:', searchTerm);
            }
        } catch (error) {
            console.error('Error fetching specific asset:', error);
        }
    };

    // Handle pending tag search when data is available
    useEffect(() => {
        if (pendingTagSearch) {
            // First check if the asset already exists in current table data (including additional assets)
            const existingAsset = combinedData.find(asset =>
                asset.clientAssetId && asset.clientAssetId.toLowerCase().includes(pendingTagSearch.toLowerCase())
            );

            if (existingAsset) {
                console.log("Asset found in current table data, highlighting row:", existingAsset);
                // Clear pending search since we found it locally
                setPendingTagSearch(null);
                // Reset the link reload flag
                linkTriggeredReload.current = false;

                // Highlight the row if table is ready
                if (tableRef.current) {
                    setTimeout(() => {
                        try {
                            const table = tableRef.current.table;
                            const row = table.getRow(existingAsset.id);
                            if (row) {
                                row.scrollTo();
                                handleRowClick(null, row);
                            }
                        } catch (error) {
                            console.log("Could not highlight row, table may still be loading:", error);
                        }
                    }, 100);
                }
            } else if (!linkTriggeredReload.current && pendingTagSearch.length >= 2) {
                console.log("Asset not found in current data, fetching from API:", pendingTagSearch);
                // Asset not found locally and search term is long enough, fetch and add to existing data
                fetchAndAddAsset(pendingTagSearch);
                setPendingTagSearch(null); // Clear pending search
            } else if (!linkTriggeredReload.current) {
                console.log("Search term too short for API call:", pendingTagSearch, "- minimum 2 characters required");
                setPendingTagSearch(null); // Clear pending search without API call
            }
            // If linkTriggeredReload.current is true and asset wasn't found yet,
            // keep pendingTagSearch so this effect will run again when new data arrives
        }
    }, [pendingTagSearch, data]);

    // Data fetching handled by useAssets hook



    // Map the field names to their respective options objects
    const optionsMap = {
        categoryId: categoryOptions,
        statusId: statusOptions,
        locationId: locationsMap
    };


    /**
     * Replace IDs in an object with their display names using the provided
     * mapping objects.
     */
    const replaceKeys = (obj, keys, options) => {
        keys.forEach(key => {
            if (obj[key] && options[key]) {
                const optionObj = options[key];
                obj[key] = findKeyByValue(optionObj, obj[key]);
            }
        });
        return obj;
    };

    /**
     * Reverse lookup for a value inside an object.
     */
    const findKeyByValue = (obj, value) => {
        return Object.keys(obj).find(key => obj[key] === value) || value;
    };






    // TODO disable input for custom properties if there is no key in the asset so cant edit them



    const rowMenu = createRowMenu(selectedProjectId, setEditableRowIndex);

    /**
     * Handle a cell being edited within Tabulator and store the change so it
     * can be sent to the server later.
     *
     * @param {import('tabulator-tables').CellComponent} cell Tabulator cell
     */
    const handleCellEdit = (cell) => {
        const rowId = cell.getRow().getData().id;
        const fieldName = cell.getField();
        const newValue = cell.getValue();



        // console.log('Cell value updated:', rowId, fieldName, newValue);
        setShowSaveButton(true);
        setChanges((prevChanges) => {
            const updatedRow = {
                ...(prevChanges[rowId] || {}),
                [fieldName]: newValue,
            };

            // Replace key values based on the options map
            const replacedRow = replaceKeys(updatedRow, Object.keys(updatedRow), optionsMap);

            return {
                ...prevChanges,
                [rowId]: replacedRow,
            };
        });
    };

    /**
     * Persist edited assets back to the server using the PATCH API.
     *
     * @returns {Promise<string|null>} Message indicating success or `null` on
     * failure
     */
    const saveChanges = async () => {
        // console.log('Saving changes projectId:', selectedProjectId);
        const assets = Object.keys(changes).reduce((acc, id) => {
            acc[id] = changes[id];
            return acc;
        }, {});
        console.log('Changes array:', assets);
        try {
            const accessToken = await getAccessToken();
            const response = await PatchAssets({
                selectedProjectId: selectedProjectId,
                assets: assets,
                accessToken: accessToken
            });
            // console.log('Patch response:', response);
            // Print "Changes saved" only if the response is successful
            return 'Changes saved';
        } catch (error) {
            console.error('Error saving changes:', error);
            // Return null or an empty string to indicate failure
            return null;
        }
    };

    /**
     * Click handler for the "Save" button. Sends pending edits and resets UI
     * state on success.
     */
    const handleSaveClick = () => {
        saveChanges().then(result => {
            if (result) {
                console.log(result);
            }
        });
        setShowSaveButton(false);
        setAssetUpdated(true);
        setEditableRowIndex(null);
    };



    /**
     * Handle row selection events. Updates various context values so that
     * other components (viewer, relationships table) react to the newly
     * selected asset.
     *
     * @param {Event|null} e Event provided by Tabulator
     * @param {import('tabulator-tables').RowComponent} row Selected row
     */
    const handleRowClick = async (e, row) => {
        // Ensure the clicked row is visually selected immediately.
        if (tableRef.current && tableRef.current.table) {
            row.select();
        }

        // set the active for viewer and relations
        const rowData = row.getData();
        console.log("Row clicked Tag Id:", rowData);
        setSelectedAssetId(rowData.id);
        setSelectedRowId(rowData.id); // Track selected row ID
        //update input field
        setSelectedTagId(rowData.clientAssetId);
        // to zoom in with viewer
        setSelectedTagIdViewerLeft(rowData.clientAssetId);
        setSelectedTagIdViewerRight(rowData.clientAssetId);

        // Apply custom property styling based on already available customPropsMap
        // No need to fetch custom attributes on every click - they should already be available
        if (Object.keys(customPropsMap).length > 0) {
            row.getCells().forEach(cell => {
                const headerName = cell.getColumn().getField();
                if (headerName in customPropsMap) {
                    const cellElement = cell.getElement();
                    // Apply styling based on available custom properties
                    // The customPropsMap already contains the mapping we need
                    cellElement.classList.add('matching-custom-prop');
                    cellElement.classList.remove('non-matching-custom-prop');
                }
            });
        }
    };




    /**
     * Build groupBy configuration based on selected fields
     * Supports single or multi-level grouping
     */
    const buildGroupBy = () => {
        if (!groupField) return false; // No grouping

        if (!secondaryGroupField) {
            return groupField; // Single level grouping
        }

        // Multi-level grouping - return array of fields
        return [groupField, secondaryGroupField];
    };

    /**
     * Handle group label clicks - logs group info to console recursively and sets global context
     * @param {Event} _e - Click event (unused)
     * @param {Object} group - Tabulator group object
     */
    const handleGroupClick = (_e, group) => {
        console.log("Group label clicked:", group.getKey());
        const recursiveGroupData = extractGroupDataRecursive(group);
        console.log("Group data (recursive):", JSON.stringify(recursiveGroupData, null, 2));
        
        // Set the group data in global context for other components to access
        setSelectedGroupData(recursiveGroupData);
    };

    /**
     * Handle group expand/collapse - logs toggle state
     * @param {Object} group - Tabulator group object
     */
    const handleGroupToggle = (group) => {
        console.log("Group toggled:", group.getKey(), "- Visible:", group.isVisible());
    };

    /**
     * Convert row data field names to display names for better readability
     * @param {Object} rowData - Raw row data with field names
     * @returns {Object} - Row data with display names as keys
     */
    const convertRowDataToDisplayNames = (rowData) => {
        const convertedData = {};

        // Create a mapping from field names to display names using the columns
        const fieldToDisplayMap = {};
        columns.forEach(col => {
            if (col.field && col.title) {
                fieldToDisplayMap[col.field] = col.title;
            }
        });

        // Convert each field in the row data
        Object.keys(rowData).forEach(fieldName => {
            const displayName = fieldToDisplayMap[fieldName] || fieldName;
            convertedData[displayName] = rowData[fieldName];
        });

        return convertedData;
    };

    /**
     * Recursively extract group data including nested subgroups
     * @param {Object} group - Tabulator group object
     * @returns {Object} - Structured group data with nested subgroups
     */
    const extractGroupDataRecursive = (group) => {
        const groupData = {
            key: group.getKey(),
            field: group.getField(),
            visible: group.isVisible(),
            subGroups: [],
            rows: []
        };

        // Get all child groups (subgroups)
        const subGroups = group.getSubGroups();
        if (subGroups && subGroups.length > 0) {
            groupData.subGroups = subGroups.map(subGroup => extractGroupDataRecursive(subGroup));
        }

        // Get direct row data (not in subgroups) and convert field names to display names
        const rows = group.getRows();
        if (rows && rows.length > 0) {
            groupData.rows = rows.map(row => convertRowDataToDisplayNames(row.getData()));
        }

        return groupData;
    };

    const options = {
        layout: 'fitColumns',
        tooltips: true,
        addRowPos: 'top',
        history: false,
        // pagination: 'local',
        paginationSize: 10,
        paginationSizeSelector: [5, 10, 25, 50, 200], //enable page size select element with these options
        pagination: true,
        movableColumns: false,
        resizableRows: false,

        rowContextMenu: rowMenu,

        // Enable row selection by setting the integer
        // TODO enable multipe row selection, how handle that in setSelectedTagId?
        selectable: 1, // Allow single row selection
        selectableRangeMode: "click", // Select row on click

        // Built-in persistence for column visibility and other table state
        persistence: {
            columns: ["visible"], // Save column visibility state
        },
        persistenceID: `assetColumns_${selectedProjectId}`, // Unique ID per project for this table's persistence

        // Grouping configuration
        groupStartOpen: false,
        // Dynamic groupBy configuration
        ...(groupField && { groupBy: buildGroupBy() }),
        
        // Group interaction callbacks
        groupClick: handleGroupClick,
        groupToggle: handleGroupToggle,
    };



    /**
     * Update the search input value. The actual asset query is triggered from
     * other effects so typing does not immediately reload the table.
     */
    const handleInputChange = (event) => {
        setSelectedTagId(event.target.value);
        // Don't immediately set assetFetchOptions - let the smart search logic handle it
        console.log('Selected Tag ID handleInputChange:', selectedTagId);
    };
    /** Clear the search input and reset query options to the current category. */
    const handleClearInput = () => {
        setSelectedTagId('');
        setAssetFetchOptions(`?includeCustomAttributes=true&filter[categoryId]=${selectedCategoryId}&limit=200`);

    };

    /**
     * Clear all cached table data and fetch a fresh copy from the API.
     * This is used when the user explicitly requests the latest
     * information from the server, bypassing any locally cached rows.
     */
    const handleGetFromServer = () => {
        // Remove any current rows so Tabulator will render new data only
        setTableData([]);
        // Also clear additional assets that might have been appended
        setAdditionalAssets([]);
        // Trigger the same reload logic as the "Get assets" button
        handleReloadClick();
    };

    /**
     * Trigger a new asset fetch. If a fetch is already in progress the user is
     * given the option to cancel it. Search parameters are constructed based on
     * the current input field or selected category.
     */
    const handleReloadClick = () => {
        console.log('Button clicked - isLoading:', isLoading, 'canCancel:', canCancel, 'progress:', progress);

        // If currently loading, show confirmation dialog
        if (isLoading && canCancel) {
            const shouldStop = window.confirm(
                `Asset loading is in progress (${progress.currentCount.toLocaleString()} loaded so far).\n\nDo you want to stop the current operation?`
            );

            if (shouldStop) {
                console.log('User chose to stop - calling cancelFetch');
                cancelFetch();
            } else {
                console.log('User chose to continue loading');
            }
            // If user chooses "Cancel" in the dialog, do nothing (continue loading)
            return;
        }

        // Clear additional assets when reloading main data
        setAdditionalAssets([]);

        // Determine search mode: if text is entered in the input field use it
        // as a search term, otherwise default to the selected category.
        if (selectedTagId && selectedTagId.trim() !== '') {
            const searchText = encodeURIComponent(selectedTagId.trim());
            setAssetFetchOptions(`?filter[searchText]=${searchText}&includeCustomAttributes=true&includeDeleted=false&limit=200`);
            console.log('Set assetFetchOptions for search text:', selectedTagId);
        } else {
            setAssetFetchOptions(`?includeCustomAttributes=true&filter[categoryId]=${selectedCategoryId}&limit=200`);
            console.log('Reset assetFetchOptions to category-based query for selectedCategoryId:', selectedCategoryId);
        }

        // Normal behavior: start new fetch
        console.log('Starting new fetch - toggling reloadFlag');
        setReloadFlag(prevFlag => !prevFlag);
    };

    // Expose handleReloadClick function to parent component
    useImperativeHandle(ref, () => ({
        handleReloadClick
    }));

    /**
     * Compute the label for the "Get assets" button based on loading state and
     * how many assets have already been fetched.
     */
    const getButtonText = () => {
        if (isLoading) {
            if (progress.currentCount > 0) {
                return progress.hasMore
                    ? `Loading... (${progress.currentCount.toLocaleString()})`
                    : `Finishing... (${progress.currentCount.toLocaleString()})`;
            }
            return 'Loading...';
        }

        if (combinedData.length > 0) {
            const additionalCount = additionalAssets.length;
            if (additionalCount > 0) {
                return `Get assets (${data.length.toLocaleString()} + ${additionalCount} found)`;
            }
            return `Get assets (${combinedData.length.toLocaleString()})`;
        }

        return 'Get assets';
    };

    const ASSET_MODE_KEY = `asset_mode_${selectedProjectId}`;

    // Save asset mode to localStorage
    const saveAssetMode = (mode) => {
        try {
            localStorage.setItem(ASSET_MODE_KEY, mode);
        } catch (error) {
            console.error('Error saving asset mode to localStorage:', error);
        }
    };

    // Load asset mode from localStorage
    const loadAssetMode = () => {
        try {
            return localStorage.getItem(ASSET_MODE_KEY) || '';
        } catch (error) {
            console.error('Error loading asset mode from localStorage:', error);
            return '';
        }
    };



    /**
     * Handle changes in the advanced options drop-down. Selected values
     * control additional features like linking to Airtable.
     */
    const handleSelectOptions = (event) => {
        const value = event.target.value;
        setSelectedAssetMode(value);

        // Reset all actions
        const newActions = {
            link2AirTable: false,
            link2Excel: false
        };

        // Enable the selected action
        if (value) {
            newActions[value] = true;
        }

        setActions(newActions);
    };

    // Check if the selected document is an Excel file
    const isExcelFile = () => {
        if (!accCurrentNode) return false;

        const fileName = accCurrentNode.displayName || accCurrentNode.name || '';
        const extension = fileName.toLowerCase().split('.').pop();
        return extension === 'xls' || extension === 'xlsx';
    };

    // Get the text for the Link Excel button based on selection and linked state
    const getLinkExcelButtonText = () => {
        if (isExcelLinked) {
            const savedSettings = loadExcelSettings();
            const isFromStorage = savedSettings && savedSettings.documentUrn &&
                accCurrentNode && accCurrentNode.itemUrn === savedSettings.documentUrn;
            return `Linked: ${linkedExcelFileName}${isFromStorage ? ' (Saved)' : ''}`;
        }
        if (!isExcelFile()) {
            return 'Link Excel (Select in document tree)';
        }
        return 'Link Excel from ACC';
    };

    // Automatically trigger asset fetching when viewers are linked and
    // the tag ID input contains a value. Only run when either value changes
    // from its previous state to avoid redundant reloads.
    const prevLinkViewState = useRef({
        enabled: linkViewersEnabled,
        tag: selectedTagId
    });

    useEffect(() => {
        const prev = prevLinkViewState.current;
        const hasTag = selectedTagId && selectedTagId.trim() !== '';

        if (
            hasTag &&
            linkViewersEnabled &&
            (prev.enabled !== linkViewersEnabled || prev.tag !== selectedTagId)
        ) {
            // Remember that this reload was triggered from a linked viewer
            linkTriggeredReload.current = true;
            handleReloadClick();
        }

        prevLinkViewState.current = {
            enabled: linkViewersEnabled,
            tag: selectedTagId
        };
    }, [linkViewersEnabled, selectedTagId]);


    return (
        <div>
            {/* Controls row - all buttons and fields in one horizontal line */}
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px', marginBottom: '20px' }}>
                <button onClick={handleReloadClick} className="btn btn-secondary">
                    {getButtonText()}
                </button>

                <div className="input-clearable" style={{ position: 'relative' }}>
                    <input
                        type="text"
                        placeholder="Enter Tag ID or any text"
                        value={selectedTagId}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ width: '200px', paddingRight: '24px' }}
                    />
                    {selectedTagId && (
                        <i
                            className="bi bi-x-circle-fill clear-input-icon"
                            onClick={handleClearInput}
                            style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#6c757d' }}
                        />
                    )}
                </div>

                <button onClick={handleGetFromServer} className="btn btn-secondary">
                    Get from server
                </button>

                {showSaveButton && (
                    <button className="btn btn-warning" onClick={handleSaveClick}>
                        Save Asset changes
                    </button>
                )}

                <CSVLink
                    data={combinedData}
                    headers={columns.map(col => ({ label: col.title, key: col.field }))}
                    filename="data.csv"
                    className="btn btn-secondary"
                >
                    Download CSV
                </CSVLink>

                <select
                    id="assetModeDropdown"
                    value={selectedAssetMode}
                    onChange={handleSelectOptions}
                    className="form-select"
                    style={{ width: 'auto', minWidth: '150px' }}
                >
                    {assetModes.map((mode) => (
                        <option
                            key={mode.value}
                            value={mode.value}
                        >
                            {mode.label}
                        </option>
                    ))}
                </select>

                {actions.link2Excel && (
                    <>
                        <button
                            onClick={handleLinkExcelClick}
                            className={`btn ${isExcelLinked ? 'btn-success' : (isExcelFile() ? 'btn-secondary' : 'btn-outline-secondary')}`}
                            disabled={!isExcelFile()}
                            title={!isExcelFile() ?
                                "Please select an Excel file (.xls or .xlsx) from the document tree to enable this feature" :
                                (isExcelLinked ? `Linked to ${linkedExcelFileName}` : "Link Excel file from ACC")
                            }
                        >
                            {getLinkExcelButtonText()}
                        </button>
                        {isExcelLinked && (
                            <button
                                onClick={handleClearExcelLink}
                                className="btn btn-outline-danger btn-sm"
                                title="Clear Excel link and saved settings"
                                style={{ marginLeft: '5px' }}
                            >
                                ✕
                            </button>
                        )}
                        <select
                            value={selectedExcelColumn}
                            onChange={handleExcelColumnSelect}
                            className="form-select"
                            style={{ width: 'auto', minWidth: '150px' }}
                        >
                            <option value="">Select link column</option>
                            {excelHeaders.map((header) => (
                                <option key={header} value={header}>{header}</option>
                            ))}
                        </select>
                    </>
                )}
            </div>

            {/* Grouping controls */}
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '16px', marginBottom: '10px' }}>
                <div>
                    <select
                        id="group-select"
                        value={groupField}
                        onChange={e => {
                            setGroupField(e.target.value);
                            // Reset secondary group if primary is cleared
                            if (!e.target.value) setSecondaryGroupField("");
                        }}
                        className="form-select"
                        style={{ width: 'auto', minWidth: '180px' }}
                    >
                        <option value="">Select Primary Group</option>
                        {getGroupingColumns.map(col => (
                            <option key={col.field} value={col.field}>{col.title}</option>
                        ))}
                    </select>
                </div>

                {/* Secondary grouping dropdown - only show if primary group is selected */}
                {groupField && (
                    <div>
                        <select
                            id="secondary-group-select"
                            value={secondaryGroupField}
                            onChange={e => setSecondaryGroupField(e.target.value)}
                            className="form-select"
                            style={{ width: 'auto', minWidth: '180px' }}
                        >
                            <option value="">Select Secondary Group</option>
                            {getGroupingColumns
                                .filter(col => col.field !== groupField) // Exclude primary group field
                                .map(col => (
                                    <option key={col.field} value={col.field}>{col.title}</option>
                                ))
                            }
                        </select>
                    </div>
                )}
            </div>

            {/* Status messages */}
            {assetUpdated && (
                <div className="alert alert-success" style={{ marginBottom: '10px' }}>
                    Asset is updated
                </div>
            )}
            <ReactTabulator
                ref={tableRef}
                data={tableData}
                columns={columns}
                options={options}
                layout={'fitData'}
                events={{
                    cellEdited: handleCellEdit,
                    rowClick: handleRowClick,
                    groupClick: handleGroupClick,
                    groupToggle: handleGroupToggle
                }}
                key={`table-${additionalAssets.length}-${groupField}-${secondaryGroupField}`}
            />
        </div>
    );
});

export default TabulatorTable;